import React from 'react';
import { Link } from 'react-router-dom';
import { PageRenderer } from '../components/site/PageRenderer';
import { Seo } from '../components/site/Seo';
import { useSiteContent } from '../context/SiteContentContext';
import { useContentfulCollection } from '../hooks/useContentfulCollection';

function formatCaseStudyDate(value) {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatCaseStudyMeta(study) {
  const keywords = study.keywords?.length ? study.keywords.slice(0, 2).join(' · ') : '';
  const date = formatCaseStudyDate(study.date || study.publishedAt);
  const fallbackOutcomeCount = !keywords && !date && study.outcomes?.length ? `${study.outcomes.length} outcomes` : '';

  return [keywords || study.sector, date, fallbackOutcomeCount].filter(Boolean).join(' · ');
}

function getCaseStudyImage(study) {
  return study.featuredImage || study.heroImage || '';
}

function getCaseStudySummary(study) {
  return study.summary || '';
}

function CaseStudyCard({ study }) {
  const href = `/case-studies/${study.slug}`;
  const meta = formatCaseStudyMeta(study);
  const image = getCaseStudyImage(study);
  const summary = getCaseStudySummary(study);

  return (
    <article className={`collection-card resource-article-card case-study-card ${image ? 'has-media' : 'no-media'}`.trim()}>
      {image ? (
        <div className='resource-article-image-shell case-study-card-image-shell'>
          <img
            src={image}
            alt={study.title}
            className='collection-card-image resource-article-image case-study-card-image'
            loading='lazy'
            decoding='async'
          />
        </div>
      ) : null}
      <div className='resource-article-copy case-study-card-copy'>
        {meta ? <p className='card-eyebrow resource-article-meta'>{meta}</p> : null}
        <h3>
          <Link className='case-study-card-link' to={href}>{study.title}</Link>
        </h3>
        {summary ? <p className='resource-article-summary case-study-card-summary'>{summary}</p> : null}
      </div>
    </article>
  );
}

export function CaseStudiesPage() {
  const { siteContent } = useSiteContent();
  const page = siteContent.pages.caseStudies;
  const { items: caseStudies } = useContentfulCollection('caseStudies', { pageSize: 50 });

  return (
    <>
      <Seo page={page} />
      <PageRenderer page={page} />
      <section className='content-section case-studies-showcase-section' id='case-study-library'>
        <div className='latest-carousel case-studies-showcase'>
          <div className='latest-carousel-header'>
          </div>

          {caseStudies.length ? (
            <div className='case-study-card-list'>
              {caseStudies.map((study) => (
                <CaseStudyCard key={study.slug} study={study} />
              ))}
            </div>
          ) : (
            <div className='case-study-empty-state'>
              <p className='section-label'>Case studies</p>
              <h3>More customer proof is being prepared.</h3>
              <p>Talk to the team about the part family, material, or post-processing bottleneck you want to evaluate.</p>
              <Link className='inline-link' to='/contact'>
                Contact Rivelin
              </Link>
            </div>
          )}
        </div>
      </section>
      <section className='content-section'>
        <div className='hero-actions' style={{ justifyContent: 'center' }}>
          <Link className='primary-cta' to='/request-demo'>Book a Demo</Link>
        </div>
      </section>
    </>
  );
}
